import React, { useMemo } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  isWeekend,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight, Pencil } from "react-bootstrap-icons";
import { eventColor } from "./calendarEventConfig";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const EVENT_ROW_H = 22; // px per stacked event row
const DAY_NUM_H = 40; // px for the date-number section at the top of each cell

function buildWeeks(month) {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

function eventsForWeek(events, weekDays) {
  const weekStart = format(weekDays[0], "yyyy-MM-dd");
  const weekEnd = format(weekDays[6], "yyyy-MM-dd");

  return events
    .filter((e) => e.start_date <= weekEnd && e.end_date >= weekStart)
    .map((e) => {
      const clippedStart = e.start_date > weekStart ? e.start_date : weekStart;
      const clippedEnd = e.end_date < weekEnd ? e.end_date : weekEnd;
      const startIdx = weekDays.findIndex(
        (d) => format(d, "yyyy-MM-dd") === clippedStart,
      );
      const endIdx = weekDays.findIndex(
        (d) => format(d, "yyyy-MM-dd") === clippedEnd,
      );
      return {
        ...e,
        startIdx: startIdx >= 0 ? startIdx : 0,
        endIdx: endIdx >= 0 ? endIdx : 6,
      };
    });
}

function EventBar({ event, index, onEdit, readOnly }) {
  const color = eventColor(event.event_type);
  const leftPct = (event.startIdx / 7) * 100;
  const widthPct = ((event.endIdx - event.startIdx + 1) / 7) * 100;

  return (
    <div
      className="group absolute flex flex-col"
      style={{
        top: DAY_NUM_H + index * EVENT_ROW_H,
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        paddingLeft: 6,
        paddingRight: 6,
      }}
    >
      <div className="h-px w-full" style={{ backgroundColor: color }} />
      <div className="flex items-center justify-between">
        <span
          className="min-w-0 flex-1 truncate text-[11px] font-medium leading-snug"
          style={{ color }}
        >
          {event.name}
        </span>
        {!readOnly && (
          <button
            type="button"
            onClick={() => onEdit(event)}
            className="ml-1 hidden shrink-0 rounded p-0.5 hover:bg-gray-100 group-hover:block dark:hover:bg-rb-dark-mode-lighter"
            title="Edit"
          >
            <Pencil style={{ fontSize: 9 }} className="text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ClientCalendar({
  events = [],
  month,
  onMonthChange,
  onEdit,
  readOnly = false,
}) {
  const weeks = useMemo(() => buildWeeks(month), [month]);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-rb-dark-mode">
      {/* Navigation: arrows left, title centered */}
      <div className="flex shrink-0 items-center border-b border-rb-widget-border px-5 py-4 dark:border-rb-dark-mode-lighter">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMonthChange(addMonths(month, -1))}
            className="flex p-2 size-10 items-center justify-center rounded-full border border-rb-widget-border bg-white text-rb-black hover:bg-gray-50 dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white dark:hover:bg-rb-dark-mode-lighter"
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            onClick={() => onMonthChange(addMonths(month, 1))}
            className="flex p-2 size-10 items-center justify-center rounded-full border border-rb-widget-border bg-white text-rb-black hover:bg-gray-50 dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white dark:hover:bg-rb-dark-mode-lighter"
          >
            <ChevronRight />
          </button>
        </div>
        <h2 className="flex-1 text-center text-sm font-semibold uppercase tracking-widest text-rb-black dark:text-white">
          {format(month, "MMMM yyyy")}
        </h2>
        {/* balance spacer matching the two arrow buttons */}
        <div style={{ width: 68 }} />
      </div>

      {/* Day-of-week header */}
      <div className="grid shrink-0 grid-cols-7 border-b border-rb-widget-border dark:border-rb-dark-mode-lighter">
        {DAYS.map((d, i) => (
          <div
            key={d}
            className={`py-2 text-center text-xs font-medium text-gray-400 dark:text-gray-500 ${
              i >= 5
                ? "bg-gray-50 dark:bg-rb-dark-mode-background"
                : "bg-white dark:bg-rb-dark-mode"
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Week rows */}
      <div className="flex flex-1 flex-col divide-y divide-rb-widget-border overflow-y-auto dark:divide-rb-dark-mode-lighter">
        {weeks.map((weekDays, wi) => {
          const weekEvents = eventsForWeek(events, weekDays);
          const minHeight =
            DAY_NUM_H + Math.max(weekEvents.length, 2) * EVENT_ROW_H + 8;

          return (
            <div
              key={wi}
              className="relative grid flex-1 grid-cols-7 divide-x divide-rb-widget-border dark:divide-rb-dark-mode-lighter"
              style={{ minHeight }}
            >
              {weekDays.map((day) => {
                const dayFmt = format(day, "yyyy-MM-dd");
                const inMonth = isSameMonth(day, month);
                const isWknd = isWeekend(day);
                const isTdy = isToday(day);

                return (
                  <div
                    key={dayFmt}
                    className={
                      isWknd
                        ? "bg-gray-50 dark:bg-rb-dark-mode-background"
                        : "bg-white dark:bg-rb-dark-mode"
                    }
                  >
                    {/* Date number section — teal tint only in this top strip for today */}
                    <div
                      className={`flex items-start px-2 pt-2 ${
                        isTdy ? "bg-rb-green/20 dark:bg-rb-green/15" : ""
                      }`}
                      style={{ height: DAY_NUM_H }}
                    >
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                          isTdy
                            ? "bg-rb-green text-white"
                            : inMonth
                              ? "text-gray-700 dark:text-white"
                              : "text-gray-300 dark:text-gray-600"
                        }`}
                      >
                        {format(day, "d")}
                      </span>
                    </div>
                  </div>
                );
              })}

              {weekEvents.map((event, i) => (
                <EventBar
                  key={event.id}
                  event={event}
                  index={i}
                  onEdit={onEdit}
                  readOnly={readOnly}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
